// app/javascript/pages/Student/Bookmarks/Index.tsx
import { useState } from 'react'
import { StudentShell } from '@/components/shared/StudentShell'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Bookmark, Trash2, Search, HelpCircle, ArrowRight, X } from 'lucide-react'
import { KatexRenderer } from '@/components/shared/KatexRenderer'
import { Link } from '@inertiajs/react'

interface BookmarkItem {
  id: number
  question_id: number
  question_text: string
  render_format: string
  difficulty: string
  question_type: string
  exam_id: number | null
  bookmarked_at: string
}

interface Props {
  bookmarks: BookmarkItem[]
}

const DIFFICULTY_CONFIG: Record<string, string> = {
  Easy: 'bg-primary/10 text-primary border-primary/20',
  Medium: 'bg-tertiary/10 text-tertiary border-tertiary/20',
  Hard: 'bg-destructive/10 text-destructive border-destructive/20',
}

export default function BookmarksIndex({ bookmarks: initialBookmarks }: Props) {
  const [bookmarks, setBookmarks] = useState(initialBookmarks)
  const [searchQuery, setSearchQuery] = useState('')
  const [difficultyFilter, setDifficultyFilter] = useState('All')

  const handleRemove = async (id: number) => {
    try {
      const res = await fetch(`/student/bookmarks/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement)?.content || ''
        }
      })
      if (res.ok) {
        setBookmarks(prev => prev.filter(b => b.id !== id))
      }
    } catch (e) {
      console.error(e)
    }
  }

  const filteredBookmarks = bookmarks.filter((bm) => {
    const matchesSearch = bm.question_text.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesDiff = difficultyFilter === 'All' || bm.difficulty === difficultyFilter
    return matchesSearch && matchesDiff
  })

  const easyCount = bookmarks.filter(b => b.difficulty === 'Easy').length
  const mediumHardCount = bookmarks.filter(b => b.difficulty === 'Medium' || b.difficulty === 'Hard').length

  return (
    <StudentShell>
      <div className="space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-16">
        {/* Hero Card Banner */}
        <div className="rounded-2xl border border-border/70 bg-card p-3.5 sm:p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="space-y-2 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-0.5 rounded-full border border-primary/25 inline-flex items-center gap-1.5 shadow-2xs">
                  <Bookmark className="h-3 w-3 text-primary" />
                  <span>Personal Revision Deck</span>
                </span>
              </div>

              <h1 className="text-lg sm:text-2xl font-bold text-foreground tracking-tight">
                Bookmarked Questions
              </h1>

              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Review and revise key questions you saved while attempting mock tests to strengthen weak areas.
              </p>
            </div>

            {/* Quick Metrics Strip */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 shrink-0 lg:max-w-md w-full">
              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-3.5 rounded-xl bg-primary/10 border border-primary/20 backdrop-blur-xs text-center sm:text-left">
                <span className="text-base sm:text-xl font-black text-primary">{bookmarks.length}</span>
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mt-0.5">Saved</span>
              </div>

              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-3.5 rounded-xl bg-secondary/10 border border-secondary/20 backdrop-blur-xs text-center sm:text-left">
                <span className="text-base sm:text-xl font-black text-foreground">{easyCount}</span>
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mt-0.5">Easy</span>
              </div>

              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-3.5 rounded-xl bg-tertiary/10 border border-tertiary/20 backdrop-blur-xs text-center sm:text-left">
                <span className="text-base sm:text-xl font-black text-foreground">{mediumHardCount}</span>
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mt-0.5">Med / Hard</span>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filter Toolbar */}
        {bookmarks.length > 0 && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
            {/* Filter Tabs */}
            <div className="flex items-center p-1 bg-muted/50 rounded-2xl border border-border/60 overflow-x-auto shrink-0 shadow-2xs [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden touch-pan-x">
              {['All', 'Easy', 'Medium', 'Hard'].map((diff) => {
                const count = diff === 'All' ? bookmarks.length : bookmarks.filter(b => b.difficulty === diff).length
                return (
                  <button
                    key={diff}
                    type="button"
                    onClick={() => setDifficultyFilter(diff)}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                      difficultyFilter === diff
                        ? 'bg-card text-foreground shadow-xs border border-border/40'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span>{diff}</span>
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-extrabold ${difficultyFilter === diff ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {count}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Search Input */}
            <div className="relative flex-1 sm:max-w-xs md:max-w-sm">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search saved questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8 h-10 rounded-xl bg-card border-border/70 text-xs sm:text-sm focus-visible:ring-primary"
              />
              {searchQuery && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground rounded-lg"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredBookmarks.length === 0 ? (
          <Card className="border border-border bg-card rounded-2xl shadow-glass-sm p-8 sm:p-12 text-center">
            <CardContent className="space-y-3 max-w-md mx-auto p-0">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto text-primary shadow-glass-xs">
                <Bookmark className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base sm:text-lg font-extrabold text-foreground">
                  {bookmarks.length === 0 ? 'No bookmarked questions yet' : 'No matching bookmarks found'}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {bookmarks.length === 0
                    ? 'Bookmark difficult or tricky questions during your mock tests to compile your personal revision deck here.'
                    : 'Try adjusting your search query or difficulty filter.'}
                </p>
              </div>
              {bookmarks.length === 0 && (
                <div className="pt-2">
                  <Button variant="outline" className="h-9 px-5 text-xs font-bold rounded-xl border-primary/40 text-primary hover:bg-primary/10 shadow-xs cursor-pointer" asChild>
                    <Link href="/student/tests">
                      Explore Mock Tests <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Bookmark Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 sm:gap-4">
            {filteredBookmarks.map(bm => (
              <Card
                key={bm.id}
                className="border border-border bg-card shadow-glass-sm rounded-2xl flex flex-col justify-between hover:border-primary/40 transition-all"
              >
                <CardContent className="p-3.5 sm:p-5 space-y-3 sm:space-y-3.5">
                  <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-3">
                    <div className="flex items-center gap-1.5">
                      <Badge className={`text-[10px] font-extrabold rounded-lg ${DIFFICULTY_CONFIG[bm.difficulty] || 'bg-muted text-muted-foreground'}`}>
                        {bm.difficulty}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] font-bold uppercase rounded-lg border-border text-muted-foreground">
                        {bm.question_type}
                      </Badge>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemove(bm.id)}
                      className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0 cursor-pointer"
                      title="Remove Bookmark"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <div className="text-xs sm:text-sm font-medium text-foreground leading-relaxed">
                    <KatexRenderer content={bm.question_text} format={bm.render_format} />
                  </div>

                  <div className="pt-2 flex items-center justify-between text-[11px] text-muted-foreground border-t border-border/40">
                    <span>Saved on {bm.bookmarked_at}</span>
                    <span className="font-bold text-primary flex items-center gap-1">
                      <HelpCircle className="h-3.5 w-3.5" /> Q#{bm.question_id}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </StudentShell>
  )
}
